/**
 * 把一段文本渲染为 PNG data URL。
 *
 * **为什么是 data URL 而不是图片路径**：`04-api-contract.md` §6.1 的
 * `qrImageUrl` 示例是 `/files/location_codes/7QK3M2XB.png`，但 §17.2 的读取校验链
 * 要求文件名匹配 `/^[a-f0-9-]{36}\.[a-z0-9]{2,5}$/`（`04-api-contract.md:2879`）——
 * 以地点码命名的文件按那条规则取不回来。前端因此**不使用** `qrImageUrl`，
 * 改为拿 `entryUrl` 现场渲染，得到的图与链路无关、永远可展示。
 *
 * 纠错级别取 M（15%）而不是 H：地点码/入口链接很短，M 已经能在磨损与反光下可读，
 * 同时让码点更大、更好扫。`margin: 1` 是符号周围的最小静默区（单位是模块数）。
 */
export async function renderQrPngDataUrl(text: string): Promise<string> {
  // 两端空白是复制粘贴带来的噪音，不是载荷的一部分：只用来做空值校验、
  // 却把原始串编进二维码，会让「同一个地点码」因为尾随空格编出不同的图，
  // 扫出来的入口地址也带空格。这里以 trim 后的结果同时做校验与编码。
  const payload = text.trim();
  if (payload === '') {
    // 空载荷会渲染出一张扫不出任何内容的图；那是配置错误，不是可展示状态。
    throw new TypeError('二维码载荷不能为空');
  }

  // **动态 import 而不是顶层静态 import**：`qrcode@1.5.4` 的 package.json
  // 没有 `sideEffects: false`，打包器因此不敢摇掉「有副作用的模块」——
  // 只要有人 `import { Button } from '@cuito/ui'`，桶文件里的 qr.ts 就会把整个
  // qrcode（编码表 + PNG 编码器，76 个模块）拖进主包。实测（/tmp 里复刻的消费者入口：
  // 只 import Button、qrcode 不 external、esbuild minify）：
  //   静态 import 42.24 kB / gzip 12.59 kB
  //   动态 import  3.24 kB / gzip  1.28 kB（未使用的 renderQrPngDataUrl 整块被摇掉）
  // 本函数本来就是 async，导出面与调用点都不变。
  const { toDataURL } = await import('qrcode');

  return toDataURL(payload, {
    type: 'image/png',
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 512,
  });
}
